export type SupportChatMessage = {
  _id?: string;
  from: "user" | "admin" | "ai" | "system" | "aiToAdmin";
  type?: "message" | "banner" | "prompt";
  text?: string;
  createdAt?: string;
  temp?: boolean;
  visibility?: boolean;
  isDeleted?: boolean;
};

export function cleanSupportMessageText(text: string): string {
  if (!text) return "";
  return text.replace(/^\[\[(AI|ADMIN)\]\]\s*/i, "");
}

export function supportMessageKey(msg: SupportChatMessage, index: number): string {
  return msg._id ?? `${msg.from}-${msg.createdAt ?? "no-ts"}-${msg.type ?? "message"}-${index}`;
}

export function normalizeIncomingSupportMessage(msg: SupportChatMessage): SupportChatMessage {
  return {
    ...msg,
    type: msg.type ?? "message",
    createdAt: msg.createdAt ?? new Date().toISOString(),
  };
}

export function isDuplicateSupportMessage(a: SupportChatMessage, b: SupportChatMessage): boolean {
  // Same DB record
  if (a._id && b._id) return a._id === b._id;

  // Can't compare without timestamps
  if (!a.createdAt || !b.createdAt) return false;

  // Same content at the same time from the same sender
  return a.from === b.from && a.createdAt === b.createdAt && a.text === b.text;
}

export function clearEscalationUiMessages(messages: SupportChatMessage[]): SupportChatMessage[] {
  return messages.filter((m) => m.type !== "banner" && m.type !== "prompt");
}

export function mergeChatHistory(
  serverMessages: SupportChatMessage[],
  liveMessages: SupportChatMessage[],
): SupportChatMessage[] {
  // Keep admin/system/ai messages, any user messages that already have an _id,
  // plus any active temp (currently sending) messages.
  const filteredLive = liveMessages.filter(
    (m) => m._id || m.temp || m.from !== "user"
  );

  if (serverMessages.length === 0) return filteredLive;

  const merged = [...serverMessages];

  for (const msg of filteredLive) {
    const alreadyExists = merged.some((m) => isDuplicateSupportMessage(m, msg));
    if (!alreadyExists) merged.push(msg);
  }

  return merged.sort(
    (a, b) => new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime(),
  );
}

export function applyIncomingSupportMessage(
  prev: SupportChatMessage[],
  msg: SupportChatMessage,
): SupportChatMessage[] {
  const normalized = normalizeIncomingSupportMessage(msg);

  // Admin messages clear banners/prompts from the UI
  const base = normalized.from === "admin" ? clearEscalationUiMessages(prev) : prev;

  const alreadyExists = base.some((m) => isDuplicateSupportMessage(m, normalized));
  if (alreadyExists) return base;

  return [...base, normalized];
}